import type { Selection } from '../model/selection.js';

/**
 * The status row's selection chip (design 111–119).
 *
 * Phase 1 deferred it and recorded why: it is `sc-if`-conditional in the design,
 * so "nothing selected" and "not built" render identically — which is what let
 * the row ship without lying. Its only source is Phase 2's selection, so this is
 * the phase that owns it.
 *
 * It sits between `ORDERS` and the escalation cluster and carries a kind, a
 * value and a clear button. The kind is there because `CTC-2E105054` and
 * `BLUE-TEL-BOREA` are not distinguishable at a glance from across a room, and
 * the chip is read from across a room.
 *
 * Escape clears it — bound in `ConsoleShell`, where Escape already unwinds one
 * layer at a time.
 */

const KIND_LABELS: Readonly<Record<Selection['kind'], string>> = Object.freeze({
  ASSET: 'ASSET',
  CONTACT: 'CONTACT',
  REGION: 'REGION',
  // A band is a zone too, and the chip is read from across a room — so it says
  // which kind of zone rather than making `GEO` and `AURELIA` look alike.
  BAND: 'BAND',
  // And a third kind of zone. `ENCLAVE` rather than `NETWORK` because the rail's
  // roster group is already `FRIENDLY NET` / `ENEMY NET` and a chip reading
  // `NETWORK · BLUE-CORE` beside a group reading `FRIENDLY NET` would be two
  // words for one thing at arm's length.
  NETWORK: 'ENCLAVE',
  MW_TRACK: 'TRACK',
});

export function SelectionChip(props: {
  readonly selection: Selection;
  readonly onClear: () => void;
}): JSX.Element {
  return (
    <div
      data-testid="selection-chip"
      data-selection-kind={props.selection.kind}
      style={{
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        height: 24,
        maxWidth: 260,
        alignSelf: 'center',
        marginLeft: 16,
        padding: '0 4px 0 8px',
        background: 'var(--accent-tint)',
        border: '1px solid var(--accent-border)',
      }}
    >
      <span
        style={{
          flex: 'none',
          fontFamily: 'var(--font-ui)',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        {KIND_LABELS[props.selection.kind]}
      </span>
      <span
        data-testid="selection-chip-value"
        style={{
          flex: 1,
          minWidth: 0,
          fontFamily: 'var(--font-data)',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '.04em',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: 'var(--accent)',
        }}
      >
        {props.selection.id}
      </span>
      <button
        type="button"
        data-testid="selection-chip-clear"
        aria-label="CLEAR SELECTION"
        title="CLEAR SELECTION (ESC)"
        onClick={props.onClear}
        style={{
          flex: 'none',
          width: 16,
          height: 16,
          padding: 0,
          background: 'transparent',
          border: 0,
          color: 'var(--text-muted)',
          fontSize: 13,
          lineHeight: 1,
          cursor: 'pointer',
        }}
      >
        ×
      </button>
    </div>
  );
}
